import {
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { FormikProps } from "formik";
import { CheckboxControl } from "formik-chakra-ui";
import React, { useEffect } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { useGlossary } from "src/hooks/useGlossary";
import * as GQL from "src/types/graphql";
import { RuleWithSchools, unavailableSchoolIds } from "../utils";
import {
  SCHOOL_CONFLICT_SUMMARY,
  useFieldError,
  useSchoolErrorById,
} from "./context";
import { RuleTip } from "./RuleTip";
import { FormEngineRuleFormData } from "./schemas";
import { School } from "./shared";

type AutoDecisionRuleProps = {
  hasPrograms: boolean;
  formik: FormikProps<FormEngineRuleFormData>;
  schools: School[];
  formEngineRules: RuleWithSchools[];
};

const AutoDecisionRule: React.FC<AutoDecisionRuleProps> = ({
  hasPrograms,
  formik,
  schools,
  formEngineRules,
}) => {
  const { glossary } = useGlossary();
  const schoolErrorById = useSchoolErrorById();
  const sourceStatusesError = useFieldError("source_statuses");

  useEffect(() => {
    formik.setFieldValue(
      "rule_type",
      GQL.form_engine_rule_type_enum.AUTO_WAITLIST
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formStatusOptions = [
    { id: GQL.form_status_enum.Submitted, label: glossary`Submitted` },
    { id: GQL.form_status_enum.Verified, label: glossary`Verified` },
    {
      id: GQL.form_status_enum.LotteryReady,
      label: glossary`Ready for lottery`,
    },
    { id: GQL.form_status_enum.Admissions, label: glossary`Admissions` },
  ];

  const schoolOptions = React.useMemo(() => {
    const takenSchoolIds = unavailableSchoolIds(
      formEngineRules,
      GQL.form_engine_rule_type_enum.AUTO_WAITLIST,
      formik.values.id
    );
    return schools
      .filter(
        (school) =>
          !takenSchoolIds.includes(school.id) ||
          formik.values.school_ids.includes(school.id)
      )
      .map((school) => ({
        id: school.id,
        label: school.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [formEngineRules, formik.values.id, formik.values.school_ids, schools]);

  return (
    <>
      <FormControl
        isRequired
        isInvalid={sourceStatusesError !== undefined}
        pb="1rem"
      >
        <FormLabel pb="0.325rem" m="0">
          Which status should the form response be in?
        </FormLabel>
        <MultiSelectInput
          size="md"
          options={formStatusOptions}
          initialValues={formik.values.source_statuses.map((status) => {
            const option = formStatusOptions.find((opt) => opt.id === status);
            return {
              id: status,
              label: option ? option.label : status,
            };
          })}
          onChange={(selectedStatuses) => {
            formik.setFieldValue(
              "source_statuses",
              selectedStatuses.map((s) => s.id)
            );
          }}
          placeholder="Select multiple"
          renderDropdownItem={(item) => <Flex key={item.id}>{item.label}</Flex>}
          error={sourceStatusesError}
        />
      </FormControl>

      <FormControl pb="1rem">
        <FormLabel pb="0.325rem" m="0">
          Which schools should this rule apply to?
        </FormLabel>
        <MultiSelectInput
          size="md"
          options={schoolOptions}
          initialValues={formik.values.school_ids.map((schoolId) => {
            const school = schools.find((s) => s.id === schoolId);
            return {
              id: schoolId,
              label: school ? school.name : schoolId,
            };
          })}
          onChange={(selectedSchools) => {
            formik.setFieldValue(
              "school_ids",
              selectedSchools.map((s) => s.id)
            );
          }}
          placeholder="Select multiple"
          error={schoolErrorById.size > 0 ? SCHOOL_CONFLICT_SUMMARY : undefined}
          isInvalidItem={(item) => schoolErrorById.has(item.id)}
          getInvalidItemMessage={(item) => schoolErrorById.get(item.id)}
        />
        <Text fontSize="sm" color="gray.600" mt="0.5rem">
          The rule will apply only to the selected schools. If no schools are
          selected, it will apply to all schools.
        </Text>
      </FormControl>

      <FormControl>
        <Tooltip
          hasArrow
          label="Auto offers can't be turned on when using programs"
          placement="top-start"
          closeOnClick={true}
          shouldWrapChildren={true}
          isDisabled={!hasPrograms}
        >
          <CheckboxControl
            isDisabled={hasPrograms}
            name="enable_automatic_offers"
            value={1}
            isChecked={formik.values.enable_automatic_offers}
          >
            Make offers
          </CheckboxControl>
        </Tooltip>
        <Text fontSize="sm" color="gray.600" ml="1.5rem">
          Automatically make offers off the top of the waitlist when there is
          capacity.
        </Text>
      </FormControl>
    </>
  );
};

export type Props = {
  hasPrograms: boolean;
  formik: FormikProps<FormEngineRuleFormData>;
  schools: School[];
  formEngineRules: RuleWithSchools[];
};
export const AutoDecision: React.FC<Props> = ({
  hasPrograms,
  formik,
  schools,
  formEngineRules,
}) => {
  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      {/* What would you like to do section */}
      <Flex direction="column" gap="0.5rem" p="1rem">
        <Heading as="h4" size="sm">
          What would you like to do?
        </Heading>
        <Flex direction="column">
          <Text fontWeight="semibold" fontSize="sm">
            Waitlist and (optionally) make offers
          </Text>
          <Text fontSize="sm" color="gray.600">
            Based on criteria, automatically waitlist students. Optionally, when
            seats are available, you can select to make offers off the top of
            the waitlist.
          </Text>
        </Flex>
      </Flex>

      {/* When would you like to add to waitlist section */}
      <Flex
        direction="column"
        gap="0.5rem"
        p="1rem"
        borderTop="1px solid"
        borderTopColor="gray.200"
      >
        <Heading as="h4" size="sm">
          When would you like to add to waitlist?
        </Heading>
        <AutoDecisionRule
          hasPrograms={hasPrograms}
          formik={formik}
          schools={schools}
          formEngineRules={formEngineRules}
        />
      </Flex>

      {/* Tip section */}
      {formik.values.rule_type && <RuleTip formik={formik} />}
    </Flex>
  );
};
